<script setup lang="ts">
import BaseButton from '@/common/components/molecules/baseButton/BaseButton.vue';
import { computed, onUnmounted, watch } from 'vue';
import StatusTag from '@/common/components/molecules/statusTag/StatusTag.vue';
import TagItem from '@/common/components/molecules/tag/TagItem.vue';
import { DetailInfoData, DetailInfoRenderType } from '@/common/utils/types';
import BaseGridRatioBarCell from '@/common/components/molecules/BaseGridRatioBarCell.vue';
import { useInternational } from '@/common/locale';
import SetupDashBoardPopup from '@/common/components/organisms/setupDashBoardPopup/SetupDashBoardPopup.vue';
import DashBoardListPopup from '@/common/components/organisms/dashBoardListPopup/DashBoardListPopup.vue';
import { useDashboardPopupEnvStore } from '@/common/stores/dashboard-popup';
import { DashboardKey, useDashboardJump } from '@/common/uses/useDashboardJump';
import lightImg from '@/common/assets/icon-setting-fill-dark.svg';
import darkImg from '@/common/assets/icon-setting-fill.svg';
import { getCurrentTheme } from '@/common/utils/commonUtils';
import { isNil } from 'lodash-es';
import BaseCustomTooltip from '@/common/components/molecules/baseCustomTooltip/BaseCustomTooltip.vue';
import CollapsibleTagList from '@/common/components/molecules/tagItemCollapse/TagItemCollapse.vue';
import { usePersistedToggle } from '@/common/composables/usePersistedToggle';

interface Props {
  envKey?: string;
  expandEnvKey?: string;
  title?: string;
  infoDataList?: DetailInfoData[];
  useTooltip?: {
    label: boolean;
  };
  keyInfo?: DashboardKey;
  isShow?: boolean;
  isNamespace?: boolean;
  useExpand?: boolean;
  useHeader?: boolean;
  useDashboardSetting?: boolean;
  toggleBtnSize?: 'xsmall' | 'small' | 'medium' | 'large';
}
interface Emit {
  (
    e: 'click-link',
    value: { field: string; value: string; namespace: string; lists: DetailInfoData[] },
  ): void;
}

const props = withDefaults(defineProps<Props>(), {
  envKey: '',
  expandEnvKey: '',
  title: '',
  infoDataList: () => [],
  useTooltip: () => ({
    label: false,
  }),
  keyInfo: () => ({
    target: '',
    targetId: '',
    namespace: '',
  }),
  useHeader: true,
  useDashboardSetting: true,
  useExpand: false,
  toggleBtnSize: 'medium',
});

const emit = defineEmits<Emit>();

const { isToggled: isExpand } = usePersistedToggle(props.expandEnvKey);

const toggleExpand = () => {
  isExpand.value = !isExpand.value;
};

const getValueContainerClass = (renderType: DetailInfoRenderType): string => {
  const defaultClass = 'information-row__value';
  const isFullType = renderType === 'custom' || renderType === 'grid';
  const fullTypeClass = isFullType ? ` ${defaultClass}--full` : '';
  const renderTypeClass =
    renderType && renderType !== 'link' ? ` ${defaultClass}--${renderType}` : '';

  return `${defaultClass}${fullTypeClass}${renderTypeClass}`;
};

const getNamespaceValue = computed<string>(
  () => props.infoDataList.find(({ field }) => field === 'namespace')?.values[0] ?? '',
);

const isValid = (value: string) => value && value !== '-';

const infoDataListExceptHide = computed(() =>
  (props?.infoDataList ?? []).filter((info) => !info.hide),
);

const theme = getCurrentTheme();
const imgSrc = computed(() => (theme === 'light' ? lightImg : darkImg));

const { fetchDashboardPopupEnvInfoForKey, deleteSetupDashboardDataByEnvKey } =
  useDashboardPopupEnvStore();
const { t } = useInternational();

const {
  isOpenDashboardSettingPopup,
  isOpenDashboardListPopup,
  dashboardPopupListCnt,
  dashboardName,
  dashboardPopupList,
  moveDashboard,
  viewDashboardList,
  dashboardTooltipRef,
  onShowDashboardTooltip,
  onHideDashboardTooltip,
  triggerButtonRef,
  popupAreaRef,
} = useDashboardJump(props.envKey);
const onClickSetting = () => {
  isOpenDashboardSettingPopup.value = true;
  if (isOpenDashboardListPopup.value) {
    isOpenDashboardListPopup.value = false;
  }
};
const setupDashboardPopupClose = async () => {
  isOpenDashboardListPopup.value = false;
  await fetchDashboardPopupEnvInfoForKey(props.envKey);
};

watch(
  () => props.isShow,
  (val) => {
    if (!val) {
      isOpenDashboardSettingPopup.value = false;
    }
  },
);

onUnmounted(() => {
  deleteSetupDashboardDataByEnvKey(props.envKey);
});
</script>

<template>
  <div :class="['detail-information', { expanded: isExpand }]">
    <div
      v-if="props.useHeader"
      class="detail-information__header"
    >
      <base-button
        v-if="useExpand"
        type="icon"
        :size="props.toggleBtnSize"
        @click="toggleExpand"
      >
        <ev-icon
          icon="icon-arrow-down"
          class="detail-information__header-expand-icon"
        />
      </base-button>
      <h3
        v-if="props.title"
        class="detail-information__header-title"
      >
        {{ props.title }}

        <slot name="header-icon-suffix" />
      </h3>
      <div
        v-if="$slots['header-action']"
        class="detail-information__header-action"
      >
        <slot name="header-action" />
      </div>
      <div
        v-if="$slots['header-help-tooltip']"
        class="detail-information__header-help-tooltip"
      >
        <slot name="header-help-tooltip" />
      </div>
      <div
        v-if="$slots['header-text']"
        class="detail-information__header-text"
      >
        <slot name="header-text" />
      </div>
    </div>

    <div
      v-if="isExpand"
      class="detail-information__content"
    >
      <slot name="content" />
      <template
        v-for="(data, dataIndex) in infoDataListExceptHide"
        :key="data.field ?? dataIndex"
      >
        <dl
          v-if="data.renderType !== 'customRow'"
          :class="['information-row', `information-row--${data.field}`]"
        >
          <dt
            :title="props.useTooltip.label ? data.label : ''"
            class="information-row__label"
          >
            <slot
              v-if="data.useIconInLabel"
              :name="data.field"
              :data="data"
              :label="data.label"
            />
            <span v-else> {{ data.label }}</span>
          </dt>
          <dd :class="getValueContainerClass(data.renderType!)">
            <slot
              v-if="data.renderType === 'grid' || data.renderType === 'grid-fill'"
              :name="data.field"
              :data="data"
            />
            <template v-else-if="data.renderType === 'label'">
              <collapsible-tag-list
                v-if="(data.values?.length ?? 0) > 0"
                class="information-row__value--chips"
                :items="data.values"
                item-size="xsmall"
              />
              <span v-else> - </span>
            </template>
            <template v-else>
              <div
                v-for="(value, index) in data.values"
                :key="`${data.field}-val-${index}`"
                class="information-row__value--value"
              >
                <span
                  v-if="data.renderType === 'link' && isValid(value)"
                  class="information-row__value--link"
                  @click="
                    () =>
                      emit('click-link', {
                        field: data.field,
                        value,
                        namespace: getNamespaceValue,
                        lists: props.infoDataList,
                      })
                  "
                >
                  {{ value }}
                  <slot
                    v-if="value"
                    name="link-suffix"
                  />
                </span>
                <div
                  v-else-if="
                    (data.field === 'name' ||
                      data.field === 'instanceName' ||
                      (data.field === 'namespace' && props.isNamespace)) &&
                    data.values[0] &&
                    props.keyInfo.targetId
                  "
                  class="detail-name"
                >
                  <slot
                    v-if="data.renderType === 'customNameValue'"
                    :name="data.field"
                    :data="data"
                    :value="value"
                  />
                  <span
                    v-else
                    :title="value"
                  >
                    {{ value }}
                  </span>
                  <div class="detail-name__btn-area">
                    <tag-item
                      ref="triggerButtonRef"
                      html-type="button"
                      icon="icon-arrow-right"
                      size="small"
                      :class="['border-button', { primary: dashboardPopupListCnt > 1 }]"
                      :disabled="dashboardPopupListCnt === 0"
                      @click="(event) => viewDashboardList(event, infoDataList, props.keyInfo)"
                      @mouseenter="(e) => onShowDashboardTooltip(e)"
                      @mouseleave="(e) => onHideDashboardTooltip(e)"
                    >
                      {{ dashboardName === '' ? t('WORD.VIEW_DETAIL') : dashboardName }}
                    </tag-item>
                    <div class="dashboard-list-popup-wrapper">
                      <dash-board-list-popup
                        v-if="isOpenDashboardListPopup"
                        ref="popupAreaRef"
                        :dashboard-list="dashboardPopupList"
                        :info-data-list="props.infoDataList"
                        :move-dashboard="moveDashboard"
                        :key-info="props.keyInfo"
                      />
                    </div>
                  </div>
                  <div class="detail-name__setting-button">
                    <img
                      :src="imgSrc"
                      width="16"
                      height="16"
                      :title="t('WORD.SETUP_DETAIL')"
                      alt="clickIcon"
                      @click="onClickSetting"
                    />
                  </div>
                </div>
                <status-tag
                  v-else-if="data.renderType === 'status' && value"
                  :type="value"
                >
                  {{ value }}
                </status-tag>
                <base-grid-ratio-bar-cell
                  v-else-if="data.renderType === 'ratio'"
                  :ratio="+value"
                  has-unit
                />
                <slot
                  v-else-if="data.renderType === 'customValue'"
                  :name="data.field"
                  :data="data"
                  :value="value"
                />
                <span v-else-if="isNil(value) || value === ''"> - </span>
                <span
                  v-else
                  class="information-row__value--value-text"
                >
                  {{ value }}
                </span>
              </div>
            </template>
          </dd>
        </dl>
        <slot
          v-else
          :name="data.field"
          :data="data"
        />
      </template>
      <slot name="bottom-content" />
    </div>
    <setup-dash-board-popup
      v-if="props.useDashboardSetting"
      v-model:is-show="isOpenDashboardSettingPopup"
      :env-key="envKey"
      @close="setupDashboardPopupClose"
    />
    <base-custom-tooltip
      ref="dashboardTooltipRef"
      custom-class="dashboard-msg-tooltip"
      use-deselect
      use-hover-event
    >
      <p>{{ t('MESSAGE.NO_PAGE_DASHBOARD') }}</p>
    </base-custom-tooltip>
  </div>
</template>

<style lang="scss" scoped>
.detail-information {
  &__header {
    display: flex;
    position: relative;
    gap: 6px;
    align-items: center;

    &-action {
      position: absolute;
      top: 4px;
      right: 4px;
    }

    &-text {
      display: flex;
      flex: 1;
      justify-content: flex-end;
    }

    &-expand-icon {
      transition: transform $animate-fast;
    }
  }

  &.expanded {
    > .detail-information__header > .ev-button > .detail-information__header-expand-icon {
      transform: rotate(-180deg);
    }
  }

  &:not(.expanded) {
    border-bottom: none;
  }
}
.information-row__value--chips {
  display: flex;
  align-items: center;
  width: 100%;
  min-width: 0;
}
.information-row__value--label {
  overflow: visible;
  white-space: normal;
}
.information-row__value--value {
  display: flex;
  flex-direction: row;
  gap: 8px;
  align-items: center;
  width: 100% !important;

  &-text {
    width: 100%;
  }

  .detail-name {
    display: flex;
    flex-direction: row;
    gap: 8px;
    align-items: center;
    max-width: 100%;
    &__btn-area {
      position: relative;
      height: 20px !important;
      cursor: pointer;
      white-space: nowrap;
    }
    .dashboard-list-popup-wrapper {
      position: absolute;
      top: 100%;
      left: 0;
      z-index: 10;
    }
    &__button {
      display: flex;
      flex-direction: column;
      gap: 10px;
      align-items: flex-start;
      justify-content: center;
      height: 20px !important;
      cursor: pointer;
    }
    &__setting-button {
      height: 16px;
      cursor: pointer;
    }
  }
}
</style>

<style lang="scss">
.information-row__value {
  .detail-name {
    > span {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
}
</style>
